/* ============================================================
   CHRONCES — <cr-add-to-cart>
   Ajout au panier depuis une carte produit.

   Même protocole que <cr-cart-insurance> : /cart/add.js + Section
   Rendering API + CartLinesUpdateEvent, pour que le drawer et la
   pastille du header se mettent à jour comme sur une action native.
   ============================================================ */

import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

const SECTION_ID = 'cart-drawer-section';

class CrAddToCart extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  onClick = async (event) => {
    event.preventDefault();
    if (this.busy) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.busy = true;
    this.classList.add('is-busy');
    const initialLabel = this.button.textContent;
    this.button.textContent = this.dataset.labelLoading || '…';

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ id: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: SECTION_ID,
        sections_url: window.location.pathname,
      });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));

      // On vérifie le statut AVANT de parser : sur une erreur (429, 5xx,
      // page mot de passe…) Shopify renvoie du HTML, et un JSON.parse
      // direct masquerait la vraie cause derrière un « Unexpected token < ».
      if (!response.ok) {
        let reason = `HTTP ${response.status}`;
        try {
          const err = await response.clone().json();
          reason = err.description || err.message || reason;
        } catch {
          /* réponse non-JSON : on garde le code HTTP */
        }
        throw new Error(reason);
      }

      const json = await response.json();

      // ⚠️ `/cart/add.js` renvoie les LIGNES AJOUTÉES, pas un panier complet :
      // il n'a ni `currency` ni `total_price`, et `createCartFromAjaxResponse`
      // lève alors « Currency code is required with currency style ».
      // On relit donc le panier réel pour alimenter l'événement.
      // (`/cart/update.js`, lui, renvoie bien un panier complet — d'où la
      // différence avec <cr-cart-insurance>.)
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: json.sections, itemCount: cart.item_count, source: 'cr-add-to-cart', didError: false },
      });

      if (json.sections?.[SECTION_ID]) morphSection(SECTION_ID, json.sections[SECTION_ID], { mode: 'hydration' });

      this.button.textContent = this.dataset.labelDone || 'Ajouté ✓';
      this.classList.add('is-done');
      // On ouvre le drawer pour que le client voie ce qu'il vient d'ajouter.
      if (this.dataset.openDrawer !== 'false') {
        document.querySelector('#cart-drawer')?.open?.();
      }
      setTimeout(() => {
        this.button.textContent = initialLabel;
        this.classList.remove('is-done');
      }, 2000);
    } catch (error) {
      console.error('[cr-add-to-cart]', error);
      deferred.reject(error);
      this.button.textContent = this.dataset.labelError || 'Indisponible';
      setTimeout(() => { this.button.textContent = initialLabel; }, 2500);
    } finally {
      this.classList.remove('is-busy');
      this.busy = false;
    }
  };
}

if (!customElements.get('cr-add-to-cart')) customElements.define('cr-add-to-cart', CrAddToCart);
